import {useState, useMemo, useEffect, useCallback} from 'react'

import {Button, Heading} from '@primer/react'

import {useSearchParams} from '@github-ui/react-router'

import type {ServersData, Server} from '../../../../types'

import {GridSection} from '../GridSection/GridSection'

import styles from './Content.module.css'

type Props = {
  searchQuery: string
  data: ServersData
}

export function Content({searchQuery, data}: Props) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [cursorHistory, setCursorHistory] = useState<string[]>([''])

  const currentCursor = searchParams.get('cursor') || ''
  const hasSearchTerm = searchQuery.trim().length > 0

  // Memoized server normalization function
  const normalizeServer = useCallback(
    (server: Server) => ({
      ...server,
      uses_custom_opengraph_image: server.uses_custom_opengraph_image || false,
    }),
    [],
  )

  // Track cursor history for pagination
  useEffect(() => {
    if (currentCursor && !cursorHistory.includes(currentCursor)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCursorHistory(prev => [...prev, currentCursor])
    }
  }, [currentCursor, cursorHistory])

  // Reset cursor history when search changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCursorHistory([''])
  }, [searchQuery])

  // Transform and filter MCPs based on search state
  const {regularMcps, searchResults} = useMemo(() => {
    const transformedMcps = (data.servers || [])
      .map(normalizeServer)
      .sort((a, b) => (b.stargazer_count || 0) - (a.stargazer_count || 0))

    if (!hasSearchTerm) {
      return {
        regularMcps: transformedMcps,
        searchResults: [],
      }
    }

    // When searching, use server-provided results directly (no client-side filtering)
    return {
      regularMcps: [],
      searchResults: transformedMcps,
    }
  }, [data.servers, hasSearchTerm, normalizeServer])

  // Determine which MCPs to display
  const currentMcps = hasSearchTerm ? searchResults : regularMcps

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(current => {
        const newSearchParams = new URLSearchParams(current)
        for (const [key, value] of Object.entries(updates)) {
          if (value === null) {
            newSearchParams.delete(key)
          } else {
            newSearchParams.set(key, value)
          }
        }
        return newSearchParams
      })
    },
    [setSearchParams],
  )

  const handleNextPage = useCallback(() => {
    if (data.metadata.next_cursor) {
      updateSearchParams({cursor: data.metadata.next_cursor})
    }
  }, [data.metadata.next_cursor, updateSearchParams])

  const handlePreviousPage = useCallback(() => {
    const currentIndex = cursorHistory.indexOf(currentCursor)
    if (currentIndex > 0) {
      const previousCursor = cursorHistory[currentIndex - 1]
      if (previousCursor === '' || previousCursor === undefined) {
        updateSearchParams({cursor: null})
      } else {
        updateSearchParams({cursor: previousCursor})
      }
    }
  }, [cursorHistory, currentCursor, updateSearchParams])

  // Pagination state
  const currentIndex = cursorHistory.indexOf(currentCursor)
  const hasNextPage = !!data.metadata.next_cursor
  const hasPreviousPage = currentIndex > 0

  const heading = hasSearchTerm ? 'Search results' : 'All MCP servers'

  return (
    <div className={styles.container}>
      <div>
        {currentMcps.length > 0 ? (
          <div className="d-flex flex-items-center mb-4">
            <Heading as="h2" className="f3 mr-2">
              {heading}
            </Heading>

            <span className="Counter">{currentMcps.length}</span>
          </div>
        ) : (
          <Heading as="h2" className="f3 mb-4">
            {heading}
          </Heading>
        )}

        {currentMcps.length > 0 ? <GridSection mcps={currentMcps} /> : null}

        {hasSearchTerm && searchResults.length === 0 && (
          <p className="color-fg-muted mb-0">
            No MCPs found matching &quot;{searchQuery}&quot;. Try a different search term.
          </p>
        )}
      </div>

      {(hasNextPage || hasPreviousPage) && (
        <div className={styles.pagination}>
          <Button variant="invisible" size="small" disabled={!hasPreviousPage} onClick={handlePreviousPage}>
            Previous
          </Button>
          <Button variant="invisible" size="small" disabled={!hasNextPage} onClick={handleNextPage}>
            Next
          </Button>
        </div>
      )}
    </div>
  )
}

try{ Content.displayName ||= 'Content' } catch {}